const FALLBACK_TIMEZONES = [
  "UTC",
  "Africa/Cairo",
  "America/Los_Angeles",
  "America/New_York",
  "America/Sao_Paulo",
  "Asia/Dubai",
  "Asia/Hong_Kong",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Perth",
  "Australia/Sydney",
  "Europe/Athens",
  "Europe/London",
  "Europe/Oslo",
];

export const getSupportedTimezones = () => {
  if (typeof Intl.supportedValuesOf === "function") {
    return ["UTC", ...Intl.supportedValuesOf("timeZone")];
  }

  return FALLBACK_TIMEZONES;
};

export const getBrowserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "";

export const isValidTimezone = (timezone) => {
  if (!timezone) return false;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: timezone }).format();
    return true;
  } catch {
    return false;
  }
};

const MANUAL_OFFSET_MINUTES = [0, 30, 45];

export const normaliseManualOffset = (manualOffset) => {
  if (!manualOffset || typeof manualOffset !== "object") return null;

  const hours = Number(manualOffset.hours);
  const minutes = Number(manualOffset.minutes);
  if (
    !["+", "-"].includes(manualOffset.sign) ||
    !Number.isInteger(hours) ||
    hours < 0 ||
    hours > 14 ||
    !Number.isInteger(minutes) ||
    !MANUAL_OFFSET_MINUTES.includes(minutes)
  ) {
    return null;
  }

  return { sign: manualOffset.sign, hours, minutes };
};

export const isManualOffset = (value) => Boolean(normaliseManualOffset(value));

const getManualOffsetMinutes = (manualOffset) => {
  const offset = normaliseManualOffset(manualOffset);
  if (!offset) return null;
  const totalMinutes = offset.hours * 60 + offset.minutes;
  return offset.sign === "-" ? -totalMinutes : totalMinutes;
};

const getManualOffsetLabel = (manualOffset) => {
  const offset = normaliseManualOffset(manualOffset);
  if (!offset) return "";
  return `UTC${offset.sign}${String(offset.hours).padStart(2, "0")}:${String(
    offset.minutes,
  ).padStart(2, "0")}`;
};

const resolveTimeReference = (timezone, manualOffset) => {
  const offset = normaliseManualOffset(manualOffset || timezone);
  if (offset) {
    return {
      timezone: "UTC",
      dateOffsetMinutes: getManualOffsetMinutes(offset),
      manualOffset: offset,
    };
  }

  if (!isValidTimezone(timezone)) return null;
  return { timezone, dateOffsetMinutes: 0, manualOffset: null };
};

const getReferenceDate = (date, dateOffsetMinutes) =>
  new Date(date.getTime() + dateOffsetMinutes * 60 * 1000);

const getParts = (date, timezone, options) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    ...options,
  }).formatToParts(date);

const partValue = (parts, type) =>
  parts.find((part) => part.type === type)?.value || "";

export const getTimezoneOffsetLabel = (timezone, date = new Date(), manualOffset) => {
  const reference = resolveTimeReference(timezone, manualOffset);
  if (reference?.manualOffset) return getManualOffsetLabel(reference.manualOffset);
  if (!isValidTimezone(timezone)) return "";

  const parts = getParts(date, timezone, {
    timeZoneName: "longOffset",
  });
  const offset = partValue(parts, "timeZoneName");

  if (offset === "GMT" || offset === "UTC") return "UTC";
  const match = offset.match(/(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?/);
  if (!match) return offset;

  return `UTC${match[1]}${match[2].padStart(2, "0")}:${match[3] || "00"}`;
};

export const getTimezoneAbbreviation = (timezone, date = new Date()) => {
  if (isManualOffset(timezone)) return getManualOffsetLabel(timezone);
  if (!isValidTimezone(timezone)) return "";

  const parts = getParts(date, timezone, { timeZoneName: "short" });
  return partValue(parts, "timeZoneName") || getTimezoneOffsetLabel(timezone, date);
};

export const formatTime = ({
  date,
  timezone,
  manualOffset,
  timeFormat = "24",
  includeSeconds = false,
}) => {
  const reference = resolveTimeReference(timezone, manualOffset);
  if (!reference) return "";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: reference.timezone,
    hour: "2-digit",
    minute: "2-digit",
    ...(includeSeconds ? { second: "2-digit" } : {}),
    hourCycle: timeFormat === "12" ? "h12" : "h23",
  }).format(getReferenceDate(date, reference.dateOffsetMinutes));
};

export const formatDate = ({ date, timezone, manualOffset, dateFormat = "DD/MM/YYYY" }) => {
  const reference = resolveTimeReference(timezone, manualOffset);
  if (!reference) return "";
  const parts = getParts(getReferenceDate(date, reference.dateOffsetMinutes), reference.timezone, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const values = {
    day: partValue(parts, "day"),
    month: partValue(parts, "month"),
    year: partValue(parts, "year"),
  };

  if (dateFormat === "MM/DD/YYYY") {
    return `${values.month}/${values.day}/${values.year}`;
  }
  if (dateFormat === "YYYY-MM-DD") {
    return `${values.year}-${values.month}-${values.day}`;
  }
  return `${values.day}/${values.month}/${values.year}`;
};
